import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { loginRedirect } from "@/components/Layout";
import { api, CATEGORIES } from "@/lib/api";
import { useNavigate, Link } from "react-router-dom";
import { ProfileStatusPill } from "@/components/ProfileStatusPill";
import { Crosshair, LogOut, User as UserIcon, Sparkles, ShieldCheck, Fingerprint } from "lucide-react";
import BeneficiaryManager from "@/components/BeneficiaryManager";
import { AutoDownloadSetting } from "@/pages/Community";

const PROFESSION_CATS = [
  { key: "service", label: "Service Provider (plumber, electrician, cleaner…)" },
  { key: "business_owner", label: "Business / Shop Owner" },
  { key: "job", label: "Job Seeker / Employee" },
  { key: "doctor", label: "Doctor / Medical" },
  { key: "student", label: "Student" },
  { key: "freelancer", label: "Freelancer / Consultant" },
  { key: "other", label: "Other" },
];

export default function Account() {
  const { user, setUser, logout, checkAuth } = useAuth();
  const { loc, detect, loading: locLoading } = useUserLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        mobile: user.mobile || "",
        occupation: user.occupation || "",
        profession_category: user.profession_category || "service",
        business_name: user.business_name || "",
        business_category: user.business_category || "service",
        skills: (user.skills || []).join(", "),
        bio: user.bio || "",
        city: user.city || loc.city || "",
        lat: user.lat ?? loc.lat,
        lng: user.lng ?? loc.lng,
        experience_years: user.experience_years || 0,
        hourly_rate: user.hourly_rate || 0,
        is_public: user.is_public !== false,
      });
    }
  }, [user, loc.city, loc.lat, loc.lng]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Please login</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="account-login">Login with Google</Button>
      </div>
    );
  }
  if (!form) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const useMyLocation = async () => {
    const r = await detect();
    if (r) {
      set("lat", r.lat); set("lng", r.lng); set("city", r.city);
      toast.success(`Located: ${r.city}`);
    }
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        skills: form.skills.split(",").map(s => s.trim()).filter(Boolean),
        experience_years: Number(form.experience_years) || 0,
        hourly_rate: Number(form.hourly_rate) || 0,
        lat: form.lat ? Number(form.lat) : null,
        lng: form.lng ? Number(form.lng) : null,
      };
      const r = await api.patch("/auth/profile", payload);
      setUser(r.data);
      await checkAuth();
      toast.success(r.data.auto_listing_id ? "Profile saved · your listing is live!" : "Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Your citizen profile</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">Become part of the neighbourhood</h1>
        </div>
        <div className="flex items-center gap-2">
          {user.auto_listing_id && (
            <Link to={`/listing/${user.auto_listing_id}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#E05E36]" data-testid="view-my-listing">
              <Sparkles size={14} /> View my public listing
            </Link>
          )}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 space-y-6">
        {/* Identity */}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-[#E05E36] text-white grid place-items-center font-black text-2xl overflow-hidden shrink-0">
            {user.picture ? <img loading="lazy" src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0]?.toUpperCase() || "U")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold uppercase tracking-widest text-stone-500">Google account</div>
            <div className="font-bold text-stone-900 truncate">{user.email}</div>
            <div className="mt-1.5"><ProfileStatusPill editable /></div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <Input value={form.name} onChange={e => set("name", e.target.value)} className="rounded-xl" data-testid="profile-name" />
          </Field>
          <Field label="Mobile number">
            <Input value={form.mobile} onChange={e => set("mobile", e.target.value)} placeholder="+91 98…" className="rounded-xl" data-testid="profile-mobile" />
          </Field>
        </div>

        {/* Profession */}
        <div className="pt-2 border-t border-stone-100">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-3">Work & business</div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="I am a…">
              <Select value={form.profession_category} onValueChange={v => set("profession_category", v)}>
                <SelectTrigger className="rounded-xl" data-testid="profile-professioncat"><SelectValue /></SelectTrigger>
                <SelectContent>{PROFESSION_CATS.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Occupation / role">
              <Input value={form.occupation} onChange={e => set("occupation", e.target.value)} placeholder="e.g., Plumber, Doctor, Delivery Rider" className="rounded-xl" data-testid="profile-occupation" />
            </Field>
            <Field label="Business / shop name (optional)">
              <Input value={form.business_name} onChange={e => set("business_name", e.target.value)} placeholder="e.g., Sharma General Store" className="rounded-xl" data-testid="profile-bizname" />
            </Field>
            <Field label="List me under category">
              <Select value={form.business_category} onValueChange={v => set("business_category", v)}>
                <SelectTrigger className="rounded-xl" data-testid="profile-bizcat"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Years of experience">
              <Input type="number" value={form.experience_years} onChange={e => set("experience_years", e.target.value)} className="rounded-xl" data-testid="profile-exp" />
            </Field>
            <Field label="Charges (₹ / hour, if service)">
              <Input type="number" value={form.hourly_rate} onChange={e => set("hourly_rate", e.target.value)} className="rounded-xl" data-testid="profile-rate" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Skills / tags (comma-separated)">
              <Input value={form.skills} onChange={e => set("skills", e.target.value)} placeholder="plumber, leak repair, tap fitting" className="rounded-xl" data-testid="profile-skills" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label="About you">
              <Textarea value={form.bio} onChange={e => set("bio", e.target.value)} placeholder="Tell neighbours what you do best…" className="rounded-xl" data-testid="profile-bio" />
            </Field>
          </div>
        </div>

        {/* Location */}
        <div className="pt-2 border-t border-stone-100">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-3">Where you work</div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="City">
              <Input value={form.city} onChange={e => set("city", e.target.value)} className="rounded-xl" data-testid="profile-city" />
            </Field>
            <Field label="Latitude">
              <Input type="number" step="any" value={form.lat || ""} onChange={e => set("lat", e.target.value)} className="rounded-xl" data-testid="profile-lat" />
            </Field>
            <Field label="Longitude">
              <Input type="number" step="any" value={form.lng || ""} onChange={e => set("lng", e.target.value)} className="rounded-xl" data-testid="profile-lng" />
            </Field>
          </div>
          <button
            onClick={useMyLocation}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#E05E36]"
            data-testid="profile-uselocation"
          >
            <Crosshair size={13} className={locLoading ? "animate-spin" : ""} /> Use my current GPS location
          </button>
        </div>

        {/* Visibility */}
        <div className="pt-2 border-t border-stone-100 flex items-center justify-between">
          <div>
            <div className="font-bold text-stone-900">Public profile</div>
            <div className="text-xs text-stone-500">Show me in the citizens directory & auto-create a listing from my work info.</div>
          </div>
          <Switch checked={form.is_public} onCheckedChange={v => set("is_public", v)} data-testid="profile-public" />
        </div>

        {/* Actions */}
        <div className="pt-2 flex flex-wrap gap-3">
          <Button onClick={save} disabled={saving} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] flex-1 md:flex-none" data-testid="save-profile">
            {saving ? "Saving…" : "Save profile"}
          </Button>
          <Button onClick={() => { logout(); navigate("/"); }} variant="outline" className="rounded-full gap-2" data-testid="logout-btn">
            <LogOut size={16} /> Logout
          </Button>
          <Button onClick={() => navigate("/security")} variant="outline" className="rounded-full gap-2" data-testid="open-security">
            <ShieldCheck size={16} /> Security & 2FA
          </Button>
          <Button onClick={() => navigate("/wuid")} variant="outline" className="rounded-full gap-2" data-testid="open-wuid">
            <Fingerprint size={16} /> World Unique ID (WUID)
          </Button>
          <Button onClick={() => navigate("/onboarding-ai")} variant="outline" className="rounded-full gap-2" data-testid="open-onboarding-ai">
            <Sparkles size={16} /> AI Auto-Fill My Profile
          </Button>
        </div>
      </div>

      {/* Beneficiary management (genuine account for receiving funds) */}
      <div className="mt-6">
        <BeneficiaryManager />
        <AutoDownloadSetting />
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-stone-500">{label} {required && <span className="text-[#E05E36]">*</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
